import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import ReactMarkdown from 'react-markdown';
import * as api from '../services/api';

interface TermsAndConditionsModalProps {
  show: boolean;
  onHide: () => void;
  onAgree: () => void;
  /** Current ToS version from backend (e.g. "1.2"). */
  termsVersion?: string;
}

const Overlay = styled.div<{ $show: boolean }>`
  display: ${(p) => (p.$show ? 'flex' : 'none')};
  position: fixed;
  inset: 0;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
`;

const ModalBox = styled.div`
  background: white;
  border-radius: 12px;
  max-width: 640px;
  width: 90%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const Header = styled.div`
  padding: 20px 24px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 1.25rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0 4px;
  &:hover {
    color: #111;
  }
`;

const ScrollArea = styled.div`
  overflow-y: auto;
  padding: 20px 24px;
  flex: 1;
  max-height: 400px;
  border: 1px solid #e5e7eb;
  margin: 0 24px;
  border-radius: 8px;
  background: #f8fafc;
`;

/** Rendered markdown from the .md file — matches docs/TERMS_OF_SERVICE_*.md structure */
const TermsMarkdown = styled.div`
  font-size: 0.9rem;
  line-height: 1.5;
  color: #4b5563;
  & h1 {
    font-size: 1.1rem;
    margin: 0 0 8px;
    color: #111;
  }
  & h2 {
    font-size: 1rem;
    margin: 1em 0 6px;
    color: #374151;
  }
  & p {
    margin: 0 0 8px;
  }
  & ul {
    margin: 4px 0 12px;
    padding-left: 1.25em;
  }
  & li {
    margin: 2px 0;
  }
  & hr {
    border: none;
    border-top: 1px solid #e5e7eb;
    margin: 12px 0;
  }
  & strong {
    color: #374151;
  }
`;

const LoadingMessage = styled.p`
  color: #6b7280;
  margin: 0;
`;

const ErrorMessage = styled.p`
  color: #b91c1c;
  margin: 0;
`;

const Footer = styled.div`
  padding: 20px 24px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
`;

const AgreeButton = styled.button<{ $disabled: boolean }>`
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  border: none;
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  background: ${(p) => (p.$disabled ? '#9ca3af' : '#2563eb')};
  color: white;
  &:hover:not(:disabled) {
    background: #1d4ed8;
  }
`;

const ScrollHint = styled.p`
  text-align: center;
  font-size: 0.85rem;
  color: #6b7280;
  margin: 8px 0;
`;

const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = ({ show, onHide, onAgree, termsVersion = '1.2' }) => {
  const [canAgree, setCanAgree] = useState(false);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [content, setContent] = useState<string | null>(null);
  const [contentError, setContentError] = useState<string | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!show || !termsVersion) return;
    setContent(null);
    setContentError(null);
    setHasScrolledToBottom(false);
    api
      .getTermsContent(termsVersion)
      .then((res) => {
        if (res.success && res.data?.content) setContent(res.data.content);
        else setContentError('Could not load terms.');
      })
      .catch(() => setContentError('Could not load terms.'));
  }, [show, termsVersion]);

  const handleScroll = () => {
    if (scrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setHasScrolledToBottom(scrollTop + clientHeight >= scrollHeight - 10);
    }
  };

  useEffect(() => {
    setCanAgree(hasScrolledToBottom);
  }, [hasScrolledToBottom]);

  const handleAgree = () => {
    if (canAgree) {
      onAgree();
      onHide();
    }
  };

  if (!show) return null;

  return (
    <Overlay $show={show} onClick={onHide}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Terms & Conditions</Title>
          <CloseBtn type="button" onClick={onHide} aria-label="Close">
            &times;
          </CloseBtn>
        </Header>
        <ScrollArea ref={scrollRef} onScroll={handleScroll}>
          {contentError && <ErrorMessage>{contentError}</ErrorMessage>}
          {!content && !contentError && <LoadingMessage>Loading terms…</LoadingMessage>}
          {content && (
            <TermsMarkdown>
              <ReactMarkdown>{content}</ReactMarkdown>
            </TermsMarkdown>
          )}
        </ScrollArea>
        {!hasScrolledToBottom && content && <ScrollHint>Please scroll to the bottom to enable the Agree button.</ScrollHint>}
        <Footer>
          <AgreeButton type="button" $disabled={!canAgree} onClick={handleAgree}>
            {canAgree ? 'I Agree to Terms and Conditions' : 'Scroll to Bottom to Agree'}
          </AgreeButton>
        </Footer>
      </ModalBox>
    </Overlay>
  );
};

export default TermsAndConditionsModal;
